import React, { useEffect, useState } from 'react';
import { BiDotsHorizontal, BiMinus, BiSolidMicrophone, BiSolidSend, BiVolumeFull } from 'react-icons/bi';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';

import { IAiAppearance } from '../types';
import BiLouiseSmallGray from './icons/BiLouiseSmallGray';
import { widgetUtils } from './utils';
import WidgetText from './WidgetText';

const TextWidget = ({
  messages,
  data: appearance,
}: {
  messages: {
    userType: 'bot' | 'user';
    data: { type: string; value: string };
  }[];
  data: IAiAppearance;
}) => {
  const [avatarSrc, setAvatarSrc] = useState('');

  const renderMessage = (userType: string, message: { data: { type: string; value: string } }, appearance: IAiAppearance) => {
    if (message.data?.type === 'audio') {
      return null;
    } else
      return (
        <div className="tw-relative">
          <WidgetText
            data={message.data?.value}
            variant={userType as any}
            theme={appearance.theme}
            color={widgetUtils.bgColorMapper[appearance?.color] ?? appearance?.color}
          />
          {userType === 'bot' && appearance.voiceResponse && (
            <div className="tw-absolute tw-right-[-10px] tw-bottom-[-10px] tw-z-[99] ">
              <div className="tw-p-1 tw-shadow-xl tw-rounded-full tw-bg-white">
                <BiVolumeFull className="tw-text-xl tw-text-black" />
              </div>
            </div>
          )}
        </div>
      );
  };

  useEffect(() => {
    if (typeof appearance.logo === 'object') {
      const reader = new FileReader();
      reader.readAsDataURL(appearance.logo);
      reader.onload = () => {
        setAvatarSrc(reader.result as string);
      };
      reader.onerror = error => console.log(error);
    } else {
      setAvatarSrc(appearance.logo);
    }
  }, [appearance.logo]);

  const getName = () => {
    return appearance.displayedName || 'Louise';
  };

  const themeBg = appearance.theme === 'light' ? 'tw-bg-gray-50 tw-text-gray-700' : 'tw-bg-gray-800 tw-text-gray-100';

  const themeBorderColor = appearance.theme === 'light' ? 'tw-border-gray-200' : 'tw-border-gray-700';

  return (
    <div
      className={`cs_bot_container tw-w-full tw-h-[640px] tw-max-w-[400px] tw-relative tw-flex tw-flex-col ${themeBg} tw-rounded-2xl tw-shadow-xl`}
      id="widget"
    >
      {appearance?.customCss && <style dangerouslySetInnerHTML={{ __html: Utilities.makeAllCssImportant(appearance.customCss) }} />}

      {/* Header */}
      <div
        className={`cs_bot_head tw-h-16 tw-rounded-tl-2xl tw-rounded-tr-2xl tw-p-3 tw-shadow-md  ${
          appearance.theme == 'light' ? 'tw-bg-white' : 'tw-bg-gray-700'
        }  tw-justify-between tw-items-center tw-inline-flex`}
      >
        <div className="tw-justify-start tw-items-center tw-gap-3 tw-flex">
          {/* logo */}
          <img
            src={avatarSrc == '' ? 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png' : avatarSrc}
            alt={appearance?.displayedName}
            width={40}
            height={40}
            className="tw-min-w-[40px] tw-min-h-[40px] tw-max-w-[40px] tw-max-h-[40px] tw-bg-gradient-to-b  tw-rounded-[99px] tw-object-cover"
          />
          {/* name */}
          <div className="cs_bot_head_title tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-max-w-[150px] tw-truncate">{getName()}</div>
        </div>

        {/* actions */}
        <div className="tw-justify-end tw-items-center tw-gap-3 tw-flex">
          <button
            id="btn-bot-horizontal"
            type="button"
            disabled
            className={`tw-p-2 tw-bg-transparent tw-border-none tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex ${
              appearance.theme === 'light' ? 'tw-text-gray-800' : 'tw-text-gray-50'
            } 
            `}
          >
            <BiDotsHorizontal />
          </button>

          {!appearance.hideMinimizeButton && (
            <button
              id="btn-minus"
              type="button"
              disabled
              className={`tw-p-2 tw-bg-transparent tw-border-none tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex ${
                appearance.theme === 'light' ? 'tw-text-gray-800' : 'tw-text-gray-50'
              } 
            `}
            >
              <BiMinus />
            </button>
          )}
        </div>
      </div>

      {/* content */}
      <div
        className={`cs_bot_body tw-w-full tw-h-full tw-overflow-auto tw-mb-auto tw-p-4 tw-flex tw-flex-col tw-gap-3 ${themeBorderColor} tw-border tw-border-b-0 tw-border-solid tw-relative [&>div:first-child]:tw-mt-auto `}
        id="messages-area"
      >
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            {message.userType == 'user' && <div className="tw-user-message tw-max-w-[90%] tw-ml-auto">{renderMessage('user', message, appearance)}</div>}
            {message.userType == 'bot' && <div className="tw-bot-message tw-max-w-[90%] tw-mr-auto">{renderMessage('bot', message, appearance)}</div>}
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className={`pb-3 tw-rounded-bl-2xl tw-rounded-br-2xl  tw-border tw-border-solid tw-border-t-0 ${themeBorderColor} tw-px-4 tw-py-2`}>
        <div className="tw-w-full">
          <div className="tw-w-full tw-h-12 tw-bg-transparent tw-justify-between tw-items-center tw-inline-flex tw-gap-2">
            <React.Fragment>
              <div
                className="tw-border tw-border-solid tw-rounded-2xl tw-shadow-sm tw-px-3 tw-py-1 tw-mx-4 tw-w-full"
                style={{
                  borderColor: widgetUtils.bgColorMapper[appearance?.color ?? '#6B7280'] ?? appearance?.color,
                  backgroundColor: widgetUtils.themeBackgroundMessage(appearance.theme),
                }}
              >
                <div className="tw-w-full">
                  <div className="tw-relative tw-w-full tw- tw-bg-transparent tw-justify-between tw-items-center tw-inline-flex tw-gap-2">
                    <input
                      readOnly
                      disabled
                      id="TextInput"
                      className="tw-text-gray-800 tw-text-input tw-py-3 tw-px-2 tw-w-full tw-h-full tw-placeholder:text-gray-400 tw-bg-transparent tw-text-[16px] tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-0 tw-focus:outline-none"
                      placeholder="Enter your message..."
                      style={{ height: '44px' }}
                    ></input>
                    <div className="tw-contents">
                      <BiSolidSend className="cs_bot_send_btn tw-text-black" />
                      {appearance?.isVoiceActive && (
                        <button
                          id="btn-microphone"
                          type="button"
                          disabled
                          style={{
                            backgroundColor: widgetUtils.bgColorMapper[appearance?.color] ?? appearance?.color,
                          }}
                          className={`cs_bot_voice_btn tw-rounded-full tw-text-white tw-border-none tw-transition-all tw-p-2 tw-justify-start tw-items-start tw-gap-2.5 tw-flex`}
                        >
                          <BiSolidMicrophone size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          </div>
        </div>

        {(appearance.poweredBy || appearance.customPoweredBy?.isActive) && (
          <div
            className={`tw-h-5  tw-mt-2 tw-text-[#9CA3AF]
          tw-items-center tw-gap-0.5 tw-flex tw-justify-center tw-text-center`}
          >
            <div className="tw-text-[10px] tw-font-normal tw-font-['Lexend'] tw-leading-[15px]">Powered by</div>
            {appearance.customPoweredBy?.isActive && (
              <React.Fragment>
                {appearance.customPoweredBy?.config?.companyLogo &&
                  (appearance.customPoweredBy?.config?.companyUrl?.startsWith('https://') ? (
                    <a href={`${appearance.customPoweredBy?.config?.companyUrl}`} target="_blank" rel="noreferrer">
                      <img
                        src={appearance.customPoweredBy?.config?.companyLogo}
                        width={14}
                        height={14}
                        alt={appearance.customPoweredBy?.config?.companyName}
                        className="tw-object-contain"
                      />
                    </a>
                  ) : (
                    <img
                      src={appearance.customPoweredBy?.config?.companyLogo}
                      width={14}
                      height={14}
                      alt={appearance.customPoweredBy?.config?.companyName}
                      className="tw-object-contain"
                    />
                  ))}
                {appearance.customPoweredBy?.config?.companyName &&
                  (appearance.customPoweredBy?.config?.companyUrl?.startsWith('https://') ? (
                    <a href={`${appearance.customPoweredBy?.config?.companyUrl}`} target="_blank" rel="noreferrer">
                      <span className="tw-font-bold tw-text-[10px] tw-font-['Lexend'] tw-flex tw-items-center">
                        {appearance.customPoweredBy?.config?.companyName}
                      </span>
                    </a>
                  ) : (
                    <span className="tw-font-bold tw-text-[10px] tw-font-['Lexend']">{appearance.customPoweredBy?.config?.companyName}</span>
                  ))}
              </React.Fragment>
            )}
            {appearance.poweredBy && (
              <a
                href="https://louisebot.com"
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-1"
              >
                <div className="tw-justify-start tw-items-center tw-gap-0.5 tw-flex">
                  <BiLouiseSmallGray
                    fill={'#9CA3AF'}
                    // fill={hasBackground ? "#ffffff" : "#9CA3AF"}
                  />
                </div>
                <div className="tw-font-bold tw-text-[10px] tw-font-['Lexend']">LouiseBot</div>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(TextWidget);
